import React from 'react';
import { Row, Col } from 'reactstrap';
import { FormikInput, useTranslator } from '@front-activerse/common-components';
import { AddUserProductFormValues } from './helpers';

interface MacroFieldsProps {
  mode: AddUserProductFormValues['mode'];
  // Called on every macro edit with the full Formik field path (e.g. "per100.calories").
  onFieldChange: (fieldPath: string) => void;
}

/**
 * The five macro inputs (calories + protein/fat/carbs/fiber) for the active mode. Each mode owns
 * its own value set, so the inputs bind to `${group}.<field>` where group is per100 / perServing.
 * Per-100 caps the integer part at 3 digits ("999,99"); per-serving allows 4 ("9999,99") since a
 * serving value can legitimately exceed 999 — the real bound is validated relative to serving size.
 *
 * All five carry autoComplete="off": these come off a label the user is reading, so a browser
 * suggestion can only be wrong.
 */
const MacroFields: React.FC<MacroFieldsProps> = ({ mode, onFieldChange }) => {
  const { t } = useTranslator();
  const group = mode === 'PER_SERVING' ? 'perServing' : 'per100';
  const maxLength = mode === 'PER_SERVING' ? 7 : 6;

  return (
    <>
      <FormikInput
        name={`${group}.calories`}
        label={t('addIngredient.calories')}
        unit={t('calories.unit')}
        inputMode="decimal"
        autoComplete="off"
        maxLength={maxLength}
        translateErrMsg
        callbackOnChange={() => onFieldChange(`${group}.calories`)}
        data-testid="add-product-calories"
        data-test-id="add_product_calories"
      />

      <Row>
        <Col xs="6">
          <FormikInput
            name={`${group}.protein`}
            label={t('addIngredient.proteins')}
            unit="g"
            inputMode="decimal"
            autoComplete="off"
            maxLength={maxLength}
            translateErrMsg
            callbackOnChange={() => onFieldChange(`${group}.protein`)}
            data-testid="add-product-protein"
            data-test-id="add_product_protein"
          />
        </Col>
        <Col xs="6">
          <FormikInput
            name={`${group}.fat`}
            label={t('addIngredient.fat')}
            unit="g"
            inputMode="decimal"
            autoComplete="off"
            maxLength={maxLength}
            translateErrMsg
            callbackOnChange={() => onFieldChange(`${group}.fat`)}
            data-testid="add-product-fat"
            data-test-id="add_product_fat"
          />
        </Col>
      </Row>
      <Row>
        <Col xs="6">
          <FormikInput
            name={`${group}.carbs`}
            label={t('addIngredient.carbs')}
            unit="g"
            inputMode="decimal"
            autoComplete="off"
            maxLength={maxLength}
            translateErrMsg
            callbackOnChange={() => onFieldChange(`${group}.carbs`)}
            data-testid="add-product-carbs"
            data-test-id="add_product_carbs"
          />
        </Col>
        <Col xs="6">
          <FormikInput
            name={`${group}.fiber`}
            label={t('addIngredient.fiber')}
            unit="g"
            inputMode="decimal"
            autoComplete="off"
            maxLength={maxLength}
            translateErrMsg
            callbackOnChange={() => onFieldChange(`${group}.fiber`)}
            data-testid="add-product-fiber"
            data-test-id="add_product_fiber"
          />
        </Col>
      </Row>
    </>
  );
};

export default MacroFields;
